/**
 * Walking the navigation tree in @/nav (see there for the shape of an item), and the admin's
 * own changes to it: pages moved to another place, pages starred, and the pages visited recently.
 * A starred or recent page is {name, params}; params matter only for a page with an id in its path.
 */

/** "ComponentPrep2" -> "Component Prep 2" */
export function toWords(name) {
  return name
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
}

export function pageTitle(item) {
  return item.title || toWords(item.name);
}

/** every item under the sections, depth first, with the titles leading to it */
export function flattenNav(sections) {
  const pages = [];
  const walk = (items, path) => {
    for (const item of items) {
      const itemPath = [...path, pageTitle(item)];
      pages.push({item, path: itemPath});
      walk(item.children || [], itemPath);
    }
  };
  for (const section of sections) {
    walk(section.items, [section.title]);
  }
  return pages;
}

const wordsOf = text => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/** pages with a word starting with each word of the search, in their title or summary */
export function findPages(sections, search) {
  const searchWords = wordsOf(search || '');
  return flattenNav(sections).filter(({item}) => {
    const pageWords = wordsOf(`${pageTitle(item)} ${item.summary}`);
    return searchWords.every(word => pageWords.some(pageWord => pageWord.startsWith(word)));
  });
}


/**
 * The tree cut down to the pages that pass `isKept`. A page left out gives way to the pages
 * under it that are kept, and a section left empty goes.
 */
function keepPages(sections, isKept) {
  const keep = items => items.flatMap(item => {
    const children = keep(item.children || []);
    return isKept(item) ? [{...item, children}] : children;
  });
  return sections
    .map(section => ({...section, items: keep(section.items)}))
    .filter(section => section.items.length > 0);
}

/** the tree cut down to the routes a role can open */
export function filterNav(sections, allowed) {
  const canOpen = new Set(allowed);
  return keepPages(sections, item => canOpen.has(item.name));
}

/**
 * The tree without the pages that take an id in their path, which the menu can't link to.
 * They stay in the full tree for starred and recent visits, which have their ids.
 */
export function withoutIdPages(sections) {
  return keepPages(sections, item => !item.needs);
}

/** a role with this many pages or fewer gets them in one list rather than sections to open */
const SHORT_MENU_MAX_PAGES = 12;

export function isShortMenu(sections) {
  return flattenNav(sections).length <= SHORT_MENU_MAX_PAGES;
}

/** with fewer pages than this there is nothing to search for or rearrange */
const FEW_PAGES = 10;

export function hasFewPages(sections) {
  return flattenNav(sections).length < FEW_PAGES;
}

/** the item for a route, anywhere in the tree */
export function findItem(sections, routeName) {
  const found = flattenNav(sections).find(({item}) => item.name === routeName);
  return found && found.item;
}

/** a section's id: its title as written in @/nav, which stays when the user renames it */
export const sectionId = section => section.id || section.title;

/**
 * Where a page can be moved to: 'section:<id>' puts it at the end of a section,
 * 'page:<route name>' among that page's children.
 */
export function moveTargets(sections, routeName) {
  const moving = findItem(sections, routeName);
  const insideMoving = new Set(moving ? flattenNav([{items: [moving]}]).map(({item}) => item.name) : []);
  return [
    ...sections.map(section => ({value: `section:${sectionId(section)}`, text: section.title})),
    ...flattenNav(sections)
      .filter(({item}) => !insideMoving.has(item.name))
      .map(({item, path}) => ({value: `page:${item.name}`, text: path.join(' › ')})),
  ];
}

/**
 * The tree as the user has arranged it:
 *   customSections: [{id, title}] sections of their own, after the others
 *   moves: {routeName: target} applied in the order they were made. A move to a page that no
 *     longer exists, or into the page's own children, is skipped
 *   sectionNames: {id: title} their names for sections
 *   sectionSummaries: {id: summary} their subtitles for sections
 *   pageNames, pageSummaries: {routeName: text} their names and descriptions for pages
 * A section of their own with nothing left in it goes.
 */
export function applyMenuSettings(sections, {
  customSections = [], moves = {}, sectionNames = {}, sectionSummaries = {}, pageNames = {}, pageSummaries = {},
} = {}) {
  const tree = [
    ...JSON.parse(JSON.stringify(sections)).map(section => ({...section, id: sectionId(section)})),
    ...customSections.map(({id, title}) => ({
      id, title, icon: 'mdi-folder-outline', summary: 'A section of your own', items: [], isCustom: true,
    })),
  ];
  applyMoves(tree, moves);
  for (const {item} of flattenNav(tree)) {
    item.title = pageNames[item.name] || item.title;
    item.summary = pageSummaries[item.name] || item.summary;
  }
  return tree
    .filter(section => !section.isCustom || section.items.length > 0)
    .map(section => ({
      ...section,
      title: sectionNames[section.id] || section.title,
      summary: sectionSummaries[section.id] || section.summary,
    }));
}

/** moves pages within the tree, in place (see applyMenuSettings) */
function applyMoves(tree, moves) {
  for (const [routeName, target] of Object.entries(moves || {})) {
    const [kind, key] = target.split(/:(.*)/);
    const moving = findItem(tree, routeName);
    if (!moving) {
      continue;
    }
    const isInsideMoving = flattenNav([{items: [moving]}]).some(({item}) => item.name === key);
    const destination = kind === 'section'
      ? tree.find(section => section.id === key)
      : !isInsideMoving && findItem(tree, key);
    if (!destination) {
      continue;
    }
    const listOf = holder => holder.items || (holder.children = holder.children || []);
    for (const holder of [...tree, ...flattenNav(tree).map(({item}) => item)]) {
      const list = holder.items || holder.children || [];
      const index = list.indexOf(moving);
      if (index >= 0) {
        list.splice(index, 1);
      }
    }
    listOf(destination).push(moving);
  }
}

/** a visit added to the front of the recent pages, once per page, keeping the latest `max` */
export function rememberPage(recent, visit, max = 10) {
  return [visit, ...(recent || []).filter(page => page.name !== visit.name)].slice(0, max);
}

const samePage = (a, b) => a.name === b.name && JSON.stringify(a.params || {}) === JSON.stringify(b.params || {});

export function isStarred(starred, page) {
  return (starred || []).some(star => samePage(star, page));
}

/** stars a page, or unstars it if it already is */
export function toggleStar(starred, page) {
  return isStarred(starred, page)
    ? starred.filter(star => !samePage(star, page))
    : [...(starred || []), page];
}
